import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { AuthProvider } from "@/contexts/auth";
import { LoginGate } from "@/components/LoginGate";
import { useScreenProtection } from "@/hooks/useScreenProtection";
import { ErrorFallback } from "@/components/ErrorFallback";
import { Sentry } from "@/lib/sentry";
import { SubscriptionProvider } from "@/contexts/subscription";
import { TrackProvider } from "@/contexts/track";
import { ThemeProvider } from "@/contexts/theme";
import { LanguageProvider } from "@/contexts/language";
import { FontScaleProvider, useFontScale } from "@/contexts/fontScale";
import { FontStyleProvider, useFontStyle } from "@/contexts/fontStyle";
import { StudyComfortProvider, useStudyComfort } from "@/contexts/studyComfort";
import { SidebarClockProvider, useSidebarClock } from "@/contexts/sidebarClock";
import { isSidebarClockMode, type SidebarClockMode } from "@/lib/sidebarClock";
import { isFontScaleId, type FontScaleId } from "@/lib/fontScale";
import { isFontStyleId, type FontStyleId } from "@/lib/fontStyle";
import { useAuth } from "@/contexts/auth";
import { useTheme } from "@/contexts/theme";
import { VALID_THEMES, type AppTheme } from "@/lib/themes";
import { useLanguage } from "@/contexts/language";
import { fetchWithAuth } from "@/lib/api";
import SignUp from "@/pages/signup";
import ResetPassword from "@/pages/reset-password";
import Admin from "@/pages/admin";
import AdminQuestions from "@/pages/admin-questions";
import AdminTickets from "@/pages/admin-tickets";
import AdminPublicContacts from "@/pages/admin-public-contacts";
import ModeratorTickets from "@/pages/moderator-tickets";
import ModeratorTeam from "@/pages/moderator-team";
import Contact from "@/pages/contact";
import Privacy from "@/pages/privacy";
import Terms from "@/pages/terms";
import Waiver from "@/pages/waiver";
import Policies from "@/pages/policies";
import Install from "@/pages/install";
import AuthSuccess from "@/pages/auth-success";
import Subscription from "@/pages/subscription";
import CheckoutSuccess from "@/pages/checkout-success";
import SettingsPage from "@/pages/settings";
import ImportPage from "@/pages/import";

import Dashboard from "./pages/dashboard";
import Practice from "./pages/practice";
import Session from "./pages/session";
import Review from "./pages/review";
import Weaknesses from "./pages/weaknesses";
// CADORS — removable: delete import + Route below, unset VITE_ENABLE_CADORS, remove `features/cadors/`, nav, i18n `cadors`/`nav.cadorsDaily`.
import CadorsPage from "@/features/cadors/CadorsPage";

const queryClient = new QueryClient();

function RedirectHome() {
  const [, setLoc] = useLocation();
  useEffect(() => { setLoc("/"); }, []);
  return null;
}

function RedirectPractice() {
  const [, setLoc] = useLocation();
  useEffect(() => { setLoc("/practice"); }, []);
  return null;
}

function RedirectAnalyticsToFlightDeck() {
  const [, setLoc] = useLocation();
  useEffect(() => {
    try {
      sessionStorage.setItem("scrollToFlightLogbook", "1");
    } catch {
      /* ignore */
    }
    setLoc("/");
  }, [setLoc]);
  return null;
}

function RedirectSessionHistoryToFlightDeck() {
  const [, setLoc] = useLocation();
  useEffect(() => {
    try {
      sessionStorage.setItem("openSessionHistory", "1");
    } catch {
      /* ignore */
    }
    setLoc("/");
  }, [setLoc]);
  return null;
}

function RedirectToContact() {
  const [, setLoc] = useLocation();
  useEffect(() => { setLoc("/contact"); }, []);
  return null;
}

function RequireRole({
  allowedRoles,
  children,
}: {
  allowedRoles: string[];
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const [, setLoc] = useLocation();

  useEffect(() => {
    if (!user) return;
    const role = String(user.role ?? "").trim().toLowerCase();
    if (!allowedRoles.includes(role)) setLoc("/");
  }, [user, allowedRoles, setLoc]);

  if (!user) return null;
  const role = String(user.role ?? "").trim().toLowerCase();
  if (!allowedRoles.includes(role)) return null;
  return <>{children}</>;
}

function ProtectedRoutes() {
  return (
    <LoginGate>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/practice" component={Practice} />
        <Route path="/session/:id" component={Session} />
        <Route path="/review/:sessionId" component={Review} />
        <Route path="/weaknesses" component={Weaknesses} />
        {import.meta.env.VITE_ENABLE_CADORS === "1" ? <Route path="/cadors" component={CadorsPage} /> : null}
        <Route path="/categories" component={RedirectHome} />
        <Route path="/analytics" component={RedirectAnalyticsToFlightDeck} />
        <Route path="/session-history" component={RedirectSessionHistoryToFlightDeck} />
        <Route path="/subscription" component={Subscription} />
        <Route path="/checkout/success" component={CheckoutSuccess} />
        <Route path="/contact" component={Contact} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/admin">
          <RequireRole allowedRoles={["admin", "master_admin"]}>
            <Admin />
          </RequireRole>
        </Route>
        <Route path="/admin/questions">
          <RequireRole allowedRoles={["admin", "master_admin", "moderator"]}>
            <AdminQuestions />
          </RequireRole>
        </Route>
        <Route path="/admin/tickets">
          <RequireRole allowedRoles={["admin", "master_admin"]}>
            <AdminTickets />
          </RequireRole>
        </Route>
        <Route path="/admin/public-contacts">
          <RequireRole allowedRoles={["admin", "master_admin"]}>
            <AdminPublicContacts />
          </RequireRole>
        </Route>
        <Route path="/import" component={ImportPage} />
        <Route path="/moderator/tickets">
          <RequireRole allowedRoles={["moderator", "admin", "master_admin"]}>
            <ModeratorTickets />
          </RequireRole>
        </Route>
        <Route path="/moderator/team">
          <RequireRole allowedRoles={["moderator", "admin", "master_admin"]}>
            <ModeratorTeam />
          </RequireRole>
        </Route>
        <Route path="/inbox" component={RedirectToContact} />
        <Route component={NotFound} />
      </Switch>
    </LoginGate>
  );
}


function CaptureBanner() {
  const { capturing } = useScreenProtection();
  if (!capturing) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99998,
        background: "#0a0e1a",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "1rem",
      }}
    >
      <div style={{ fontSize: 48 }}>🔴</div>
      <div style={{
        fontFamily: "monospace", color: "#ff4444",
        fontSize: "1.1rem", fontWeight: "bold", letterSpacing: "0.1em",
      }}>
        SCREEN CAPTURE DETECTED
      </div>
      <div style={{
        fontFamily: "monospace", color: "#ff444488",
        fontSize: "0.75rem", letterSpacing: "0.05em", textAlign: "center",
        maxWidth: 340,
      }}>
        Screen sharing and recording are not permitted on this platform.<br />
        Stop the capture to continue.
      </div>
    </div>
  );
}

function PreferenceSync() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { fontScale, setFontScale } = useFontScale();
  const { fontStyle, setFontStyle } = useFontStyle();
  const { reduceMotion, comfortableSession, setReduceMotion, setComfortableSession } = useStudyComfort();
  const { mode: sidebarClockMode, setMode: setSidebarClockMode } = useSidebarClock();
  const themeRef = useRef(theme);
  const languageRef = useRef(language);
  const fontScaleRef = useRef(fontScale);
  const fontStyleRef = useRef(fontStyle);
  const reduceMotionRef = useRef(reduceMotion);
  const comfortableSessionRef = useRef(comfortableSession);
  const sidebarClockModeRef = useRef(sidebarClockMode);
  themeRef.current = theme;
  languageRef.current = language;
  fontScaleRef.current = fontScale;
  fontStyleRef.current = fontStyle;
  reduceMotionRef.current = reduceMotion;
  comfortableSessionRef.current = comfortableSession;
  sidebarClockModeRef.current = sidebarClockMode;

  useEffect(() => {
    if (!user?.id) return;
    const themeAtFetchStart = themeRef.current;
    const languageAtFetchStart = languageRef.current;
    const fontScaleAtFetchStart = fontScaleRef.current;
    const fontStyleAtFetchStart = fontStyleRef.current;
    const reduceMotionAtFetchStart = reduceMotionRef.current;
    const comfortableSessionAtFetchStart = comfortableSessionRef.current;
    const sidebarClockModeAtFetchStart = sidebarClockModeRef.current;
    let cancelled = false;
    fetchWithAuth("/api/user/account-status")
      .then((raw) => {
        if (cancelled) return;
        const data = (raw ?? {}) as {
          appTheme?: unknown;
          uiLanguage?: unknown;
          uiFontScale?: unknown;
          uiFontStyle?: unknown;
          reduceMotion?: unknown;
          comfortableSession?: unknown;
        };
        if (typeof data.appTheme === "string") {
          if ((VALID_THEMES as readonly string[]).includes(data.appTheme)) {
            const next = data.appTheme as AppTheme;
            if (
              themeRef.current === themeAtFetchStart
              && next !== themeRef.current
            ) {
              setTheme(next, { persistRemote: false });
            }
          }
        }
        if (data.uiLanguage === "en" || data.uiLanguage === "fr") {
          if (
            languageRef.current === languageAtFetchStart
            && data.uiLanguage !== languageRef.current
          ) {
            setLanguage(data.uiLanguage, { persistRemote: false });
          }
        }
        if (isFontScaleId(data.uiFontScale)) {
          if (
            fontScaleRef.current === fontScaleAtFetchStart
            && data.uiFontScale !== fontScaleRef.current
          ) {
            setFontScale(data.uiFontScale as FontScaleId, { persistRemote: false });
          }
        }
        if (isFontStyleId(data.uiFontStyle)) {
          if (
            fontStyleRef.current === fontStyleAtFetchStart
            && data.uiFontStyle !== fontStyleRef.current
          ) {
            setFontStyle(data.uiFontStyle as FontStyleId, { persistRemote: false });
          }
        }
        if (typeof data.reduceMotion === "boolean") {
          if (
            reduceMotionRef.current === reduceMotionAtFetchStart
            && data.reduceMotion !== reduceMotionRef.current
          ) {
            setReduceMotion(data.reduceMotion, { persistRemote: false });
          }
        }
        if (typeof data.comfortableSession === "boolean") {
          if (
            comfortableSessionRef.current === comfortableSessionAtFetchStart
            && data.comfortableSession !== comfortableSessionRef.current
          ) {
            setComfortableSession(data.comfortableSession, { persistRemote: false });
          }
        }
        if (isSidebarClockMode(data.sidebarClockMode)) {
          if (
            sidebarClockModeRef.current === sidebarClockModeAtFetchStart
            && data.sidebarClockMode !== sidebarClockModeRef.current
          ) {
            setSidebarClockMode(data.sidebarClockMode as SidebarClockMode, { persistRemote: false });
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id, setTheme, setLanguage, setFontScale, setFontStyle, setReduceMotion, setComfortableSession, setSidebarClockMode]);

  return null;
}

function AppInner() {
  return (
    <LanguageProvider>
    <ThemeProvider>
    <FontScaleProvider>
    <FontStyleProvider>
    <StudyComfortProvider>
    <SidebarClockProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <PreferenceSync />
          <TrackProvider>
          <SubscriptionProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <Switch>
                <Route path="/signup" component={SignUp} />
                <Route path="/auth-success" component={AuthSuccess} />
                <Route path="/reset-password" component={ResetPassword} />
                <Route path="/install" component={Install} />
                <Route path="/privacy" component={Privacy} />
                <Route path="/terms" component={Terms} />
                <Route path="/waiver" component={Waiver} />
                <Route path="/policies" component={Policies} />
                <Route>
                  <ProtectedRoutes />
                </Route>
              </Switch>
              <Toaster />
            </WouterRouter>
            <CaptureBanner />
          </SubscriptionProvider>
          </TrackProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </SidebarClockProvider>
    </StudyComfortProvider>
    </FontStyleProvider>
    </FontScaleProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <ErrorFallback
          error={error}
          componentStack={componentStack}
          eventId={eventId}
          resetError={resetError}
        />
      )}
    >
      <AppInner />
    </Sentry.ErrorBoundary>
  );
}

export default App;
